import {
  CreateEffectOptions,
  effect,
  inject,
  Inject,
  Injectable,
  Pipe,
  PipeTransform,
} from '@angular/core';
import {
  catchError,
  forkJoin,
  merge,
  Observable,
  of,
  shareReplay,
  skip,
  Subject,
  take,
  withLatestFrom,
} from 'rxjs';
import { map, mergeMap, switchMap } from 'rxjs/operators';
import { AuthService, User } from '@auth0/auth0-angular';
import { StaffService } from '../../api/staff/staff.service';
import { CustomerService } from '../../api/customer/customer.service';
import { AuthUser } from './auth.interfaces';
import { RollbarService } from '../errors/error-handler.service';
import { Customer } from '../../api/customer/customer.model';
import { Staff } from '../../api/staff/staff.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { AdminRole, StaffRole } from '../../api/staff/lib/staff.interfaces';
import { GhostingService } from './ghosting/ghosting.service';

@Injectable({ providedIn: 'root' })
export class ExtendedAuthService {
  private refreshAuthUserTrigger$ = new Subject<void>();
  private ghostingService = inject(GhostingService);

  public user$: Observable<AuthUser | null> = merge(
    this.refreshAuthUserTrigger$,
    this.authService.user$,
  ).pipe(
    withLatestFrom(this.authService.user$),
    switchMap(([, user]: [any, User | null | undefined]) => {
      if (!user || !user.sub) {
        return of(null);
      }

      const userId = this.ghostingService.getAsUser()?.id ?? user.sub;

      const customer$ = this.customerService.read(userId).pipe(
        catchError((err) => {
          if (err.status !== 404) {
            this.rollbar.error(err);
          }
          return of(null);
        }),
      );

      const staff$ = this.staffService.read(userId).pipe(
        catchError((err) => {
          if (err.status !== 404) {
            this.rollbar.error(err);
          }
          return of(null);
        }),
      );

      return forkJoin([staff$, customer$]).pipe(
        mergeMap(([staff, customer]: [Staff | null, Customer | null]) => {
          if (!customer) {
            return this.authService.logout().pipe(map(() => null));
          }
          return of(
            new (staff !== null ? Staff : Customer)({
              ...customer,
              ...staff,
              avatar: staff?.avatar ?? user.picture,
              isStaff: staff !== null,
              isOffice: staff?.staffRoles?.includes(StaffRole.Office),
            } as AuthUser) as AuthUser,
          );
        }),
      );
    }),
    shareReplay(1),
  );
  public readonly user = toSignal(this.user$, { initialValue: null });

  // User derived pipes
  public adminRoles$ = this.user$.pipe(map((user) => user?.adminRoles ?? []));
  public staffRoles$ = this.user$.pipe(map((user) => user?.staffRoles ?? []));
  public isAuthenticated$ = this.user$.pipe(map((user) => user !== null));
  public appState$ = this.authService.appState$;

  constructor(
    private authService: AuthService,
    private staffService: StaffService,
    private customerService: CustomerService,
    @Inject(RollbarService) private rollbar: RollbarService,
  ) {}

  refreshAuthUser() {
    this.refreshAuthUserTrigger$.next();
    return this.user$.pipe(
      skip(1),
      take(1),
      map(() => {}),
    );
  }

  signup() {
    return this.authService.loginWithRedirect({
      authorizationParams: { screen_hint: 'signup' },
    });
  }

  loginWithRedirect() {
    return this.login();
  }

  loginWithAction(actionType: string, data: any) {
    return this.login([{ type: actionType, data }]);
  }

  protected login(actions?: AppStateAction[]) {
    return this.authService.loginWithRedirect({
      appState: {
        target:
          window.location.pathname +
          window.location.hash +
          window.location.search,
        appStateActions: actions,
      },
    });
  }

  logout(): Observable<void> {
    return this.authService.logout();
  }
}

export interface AppStateAction {
  type: string;
  data: any;
}

export function appStateActionEffect(
  effectFn: (action: AppStateAction) => void,
  effectOptions?: CreateEffectOptions,
) {
  const auth = inject(ExtendedAuthService);
  const appStateSignal = toSignal(auth.appState$);
  const authSignal = toSignal(auth.user$);
  effect(() => {
    const appState = appStateSignal();
    const user = authSignal();
    if (user && appState && 'appStateActions' in appState) {
      for (const appStateAction of appState['appStateActions']) {
        effectFn(appStateAction);
      }
    }
  }, effectOptions);
}

export function hasStaffRole(
  user: AuthUser | null,
  role: StaffRole | StaffRole[],
): boolean {
  if (!user?.isStaff) return false;
  if (!Array.isArray(role)) return user.staffRoles.includes(role);
  return role.some((r) => user.staffRoles.includes(r));
}

export function hasAdminRole(
  user: AuthUser | null,
  role: AdminRole | AdminRole[],
): boolean {
  if (!user?.isStaff) return false;
  if (!user?.isOffice) return false;
  if (!Array.isArray(role)) return user.adminRoles.includes(role);
  return role.some((r) => user.adminRoles.includes(r));
}

@Pipe({
  name: 'isMonitor',
  standalone: true,
})
export class IsMonitorPipe implements PipeTransform {
  transform(value: AuthUser | null): boolean {
    return hasStaffRole(value, [
      StaffRole.Monitor,
      StaffRole.HeadMonitor,
      StaffRole.Teacher,
    ]);
  }
}

@Pipe({
  name: 'isExternal',
  standalone: true,
})
export class IsExternalPipe implements PipeTransform {
  transform(value: AuthUser | null): boolean {
    return hasStaffRole(value, StaffRole.External);
  }
}

@Pipe({
  name: 'isAdmin',
  standalone: true,
})
export class IsAdminPipe implements PipeTransform {
  transform(value: AuthUser | null): boolean {
    return hasAdminRole(value, [AdminRole.Office, AdminRole.Billing]);
  }
}
